// AllTerra Central — contact modals (<at-contact-modal>)
// Opens a native <dialog> with a HubSpot form (Developer Code embed) in response
// to `at:open-modal` events (at-buy-cta.js on the PDP) or to clicks on any
// `[data-at-open-modal="<key>"]` button (e.g. the industry CTA banner). One
// controller, rendered site-wide from the footer group, hosts the
// Contact / Rental / Speak-to-Expert dialogs.
//
// To keep pages fast the HubSpot portal script is NOT loaded until the first
// modal open. HubSpot's developer script hydrates the `.hs-form-html` divs that
// are present when it first loads and does not observe the DOM afterwards, so on
// first open we inject every configured dialog's target div and then load the
// portal script once. The product name + URL are prefilled once the fields
// render, detected with a MutationObserver (no dependency on HubSpot internals).

const loadedPortals = new Set();

function loadPortalScript(portalId) {
  if (loadedPortals.has(portalId)) return;
  loadedPortals.add(portalId);
  const s = document.createElement('script');
  s.src = `https://js.hsforms.net/forms/embed/developer/${portalId}.js`;
  s.defer = true;
  document.head.appendChild(s);
}

class AtContactModal extends HTMLElement {
  connectedCallback() {
    this.dialogs = new Map();
    this.querySelectorAll('[data-at-modal]').forEach((d) => this.dialogs.set(d.dataset.atModal, d));
    this.product = { title: '', url: '' };
    this.formsMounted = false;
    this.observers = [];

    this.onOpen = this.onOpen.bind(this);
    this.onTriggerClick = this.onTriggerClick.bind(this);
    document.addEventListener('at:open-modal', this.onOpen);
    document.addEventListener('click', this.onTriggerClick);

    this.querySelectorAll('[data-at-modal-close]').forEach((btn) =>
      btn.addEventListener('click', () => btn.closest('dialog')?.close())
    );
    // Close when the backdrop (the dialog element itself) is clicked.
    this.dialogs.forEach((d) =>
      d.addEventListener('click', (e) => {
        if (e.target === d) d.close();
      })
    );
  }

  disconnectedCallback() {
    document.removeEventListener('at:open-modal', this.onOpen);
    document.removeEventListener('click', this.onTriggerClick);
    this.observers.forEach((o) => o.disconnect());
  }

  onOpen(event) {
    const { modal, product } = event.detail || {};
    this.open(modal, product);
  }

  onTriggerClick(event) {
    const trigger = event.target.closest('[data-at-open-modal]');
    if (!trigger || !this.dialogs.has(trigger.dataset.atOpenModal)) return;
    event.preventDefault();
    this.open(trigger.dataset.atOpenModal);
  }

  open(modal, product) {
    const dialog = this.dialogs.get(modal);
    if (!dialog) return;
    if (product) this.product = product;
    this.mountForms();
    dialog.showModal();
  }

  // Lazy init on first open: inject every configured form's target div, then load
  // the portal script(s) once. All configured dialogs must be injected together
  // here — HubSpot only hydrates divs present when its script first loads.
  mountForms() {
    if (this.formsMounted) return;
    this.formsMounted = true;

    const portals = new Set();
    this.dialogs.forEach((dialog) => {
      const formId = dialog.dataset.hsFormId;
      const portalId = dialog.dataset.hsPortalId;
      const region = dialog.dataset.hsRegion || 'na1';
      const target = dialog.querySelector('[data-hs-target]');
      if (!formId || !portalId || !target || target.childElementCount) return;

      const div = document.createElement('div');
      div.className = 'hs-form-html';
      div.setAttribute('data-region', region);
      div.setAttribute('data-form-id', formId);
      div.setAttribute('data-portal-id', portalId);
      target.appendChild(div);
      portals.add(portalId);

      this.watchAndPrefill(target);
    });

    portals.forEach((p) => loadPortalScript(p));
  }

  // Prefill the product fields + Conversion Page URL once HubSpot renders the form.
  // The developer embed is React-based: it re-renders during hydration (and on validation
  // errors and while the visitor fills it in), which can wipe a value set too early, and it
  // reads values from React state. So we keep re-applying on every mutation for as long as
  // the form exists, AND re-apply on submit (capture phase, before HubSpot serializes).
  // The observer must not time out: a re-render after it stops can leave the URL in the DOM
  // but not in React state, so the submit-time check sees nothing to fix and HubSpot sends
  // an empty Conversion Page URL (reproduced by waiting >15s before submitting).
  watchAndPrefill(target) {
    const obs = new MutationObserver(() => this.fillProduct(target));
    obs.observe(target, { childList: true, subtree: true });
    this.observers.push(obs);
    target.addEventListener('submit', () => this.fillProduct(target), true);
    this.fillProduct(target);
  }

  fillProduct(scope) {
    const { title, url } = this.product;
    let set = false;
    // Use the native value setter + input event so React registers the change (a plain
    // `el.value =` is ignored by React-controlled inputs and dropped on the next render).
    const nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    const apply = (names, value) => {
      if (!value) return;
      for (const name of names) {
        // HubSpot field names look like "0-1/firstname" — match by suffix.
        const el = scope.querySelector(`[name$="/${name}"], [name="${name}"]`);
        // Only write empty fields: never clobber a value the user typed / selected.
        if (el && !el.value) {
          nativeSetter.call(el, value);
          el.dispatchEvent(new Event('input', { bubbles: true }));
          el.dispatchEvent(new Event('change', { bubbles: true }));
          set = true;
          break;
        }
      }
    };
    apply(['product', 'product_name', 'product_of_interest', 'products_of_interest'], title);
    apply(['product_url', 'page_url'], url);
    // Conversion Page URL — always force the CURRENT page URL. Unlike the fields above,
    // this must OVERWRITE any existing value: HubSpot pre-fills it with the last-submitted
    // value for known visitors (hutk cookie), so a fill-only-if-empty guard would leave a
    // stale URL from a previous page.
    const conv = scope.querySelector('[name$="/conversion_page_url"], [name="conversion_page_url"]');
    if (conv && conv.value !== window.location.href) {
      nativeSetter.call(conv, window.location.href);
      conv.dispatchEvent(new Event('input', { bubbles: true }));
      conv.dispatchEvent(new Event('change', { bubbles: true }));
      set = true;
    }
    return set;
  }
}

if (!customElements.get('at-contact-modal')) {
  customElements.define('at-contact-modal', AtContactModal);
}
